import { z } from 'zod';

export const loginSchema = z.object({
  email: z.email(),
  password: z.string().min(1),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const refreshSchema = z.object({
  refreshToken: z.string().min(1),
});
export type RefreshInput = z.infer<typeof refreshSchema>;

/** Response of `POST /auth/login` and `POST /auth/refresh`. */
export const tokenResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  tokenType: z.literal('Bearer'),
  expiresIn: z.number().int(), // access-token lifetime in seconds
});
export type TokenResponse = z.infer<typeof tokenResponseSchema>;

/**
 * `POST /auth/password/forgot` (M19) — request a reset link.
 *
 * The response is **always 204**, whether or not the address has an account: a 404 here would
 * be an account-enumeration oracle, which is the same reason `login` verifies against a dummy
 * hash when no user matches.
 */
export const forgotPasswordSchema = z.object({
  email: z.email(),
  // Required for the same reason `registerSchema` requires it: the route carries the
  // `turnstile` middleware, which rejects a tokenless request in production. Declaring it here
  // is what stops a caller building the full documented shape and still getting a 403.
  turnstileToken: z.string().min(1),
});
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

/**
 * `POST /auth/password/reset` (M19) — redeem a token from the e-mail.
 *
 * `min(8)` matches the registration and admin-update rules; a reset that accepted a weaker
 * password than registration would be the way round every account's password policy.
 */
export const resetPasswordSchema = z.object({
  token: z.string().min(1),
  password: z.string().min(8),
});
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;
